import { Component, OnInit, ViewChild } from '@angular/core';
import { PasswordConfirmInputComponent } from '../../shared/password-confirm-input/password-confirm-input.component';
import { AlertService } from '../../shared/alert/shared/alert.service';
import { UserDataService } from '../shared/services/user-data.service';
import { UserDataResponse } from '../shared/interfaces/login-data-response.interface';
import { UserService } from '../shared/services/user.service';
import { AlertTypes } from '../../shared/alert/shared/alert-types';
import { PasswordData } from './shared/password-data.interface';
import { EditUserPassword } from './shared/edit-user-password.interface';
import { EditUser } from '../shared/interfaces/edit-user.interface';
import { LoginService } from '../shared/services/login.service';
import { ModalService } from 'src/app/shared/modal/shared/modal.service';
import { EnableAuthenticatorResponse } from '../shared/interfaces/two-factor.interface';
import QRCode from 'qrcode';

@Component({
  selector: 'avs-user-data',
  templateUrl: './user-data-settings.component.html',
  styleUrls: ['./user-data-settings.component.css']
})
export class UserDataSettingsComponent implements OnInit {
  public userData: UserDataResponse | undefined;
  public passwordData: PasswordData = { oldPassword: '', newPassword: '' };
  public editUserPassword: EditUserPassword | undefined;
  @ViewChild(PasswordConfirmInputComponent) private passwordConfirmInput?: PasswordConfirmInputComponent;
  public authenticatorSetup?: EnableAuthenticatorResponse;
  public authenticatorQrCodeDataUrl: string | null = null;
  public twoFactorSetupCode: string = '';
  public isTwoFactorEnabled: boolean = false;
  public isTwoFactorBusy: boolean = false;

  constructor(
    private userDataService: UserDataService,
    private userService: UserService,
    private alertService: AlertService,
    public loginService: LoginService,
    public modalService: ModalService
  ) {
  }

  ngOnInit(): void {
    this.userDataService.userData.subscribe(userData => {
      this.applyUserData(userData);
    });

    this.refreshTwoFactorState();
  }

  public onTitleValueChange(newValue: string) {
    if (this.userData) {
      this.userData.title = newValue;
    }
  }

  public onFirstNameValueChange(newValue: string) {
    if (this.userData) {
      this.userData.firstName = newValue;
    }
  }

  public onLastNameValueChange(newValue: string) {
    if (this.userData) {
      this.userData.lastName = newValue;
    }
  }

  public onEmailValueChange(newValue: string) {
    if (this.userData) {
      this.userData.email = newValue;
    }
  }

  /** Receives the confirmed password from avs-password-confirm-input, or '' while the inputs do not match. */
  public onNewPasswordChange(newValue: string) {
    this.passwordData.newPassword = newValue;
  }

  public onOldPasswordChange(newValue: string) {
    this.passwordData.oldPassword = newValue;
  }

  public onSaveUserDataBtnClicked() {
    this.modalService.openDialog('Daten speichern? Sie werden automatisch abgemeldet, da eine Neuanmeldung erforderlich ist', 'Nein', 'Ja').subscribe(
      btnYes => {
        if (btnYes) {
          this.updateUserData();
          this.logOutUser();
        }
      }
    );
  }

  private logOutUser() {
    this.loginService.logout();
  }

  public onSaveNewPasswordBtnClicked() {
    if (this.passwordData.newPassword) {
      this.setNewUserPassword();
      this.updatePasswordData();
    } else {
      this.alertService.alert('Bitte geben Sie das neue Passwort zweimal übereinstimmend ein.', AlertTypes.error);
    }
  }

  private updateUserData() {
    if (this.userData) {
      const user = {
        id: this.userData.id,
        title: this.userData.title,
        firstName: this.userData.firstName,
        lastName: this.userData.lastName,
        email: this.userData.email,
        company: this.userData.company,
        isAuthorizedForDownloads: this.userData.isAuthorizedForDownloads,
        isAuthorizedForAlarms: this.userData.isAuthorizedForAlarms,
        isAlarmsActivated: this.userData.isAlarmsActivated,
        isAcousticAlarmsActivated: this.userData.isAcousticAlarmsActivated
      } as EditUser;
      this.userService.editUserData(this.userData.id, user).subscribe(() => {
        this.alertService.alert('Benutzerdaten wurden erfolgreich geändert.', AlertTypes.info);
      });
    }
  }

  private updatePasswordData() {
    if (this.editUserPassword) {
      this.userService.editPasswordData(this.userData?.id!, this.editUserPassword).subscribe(() => {
        this.alertService.alert('Das Passwort wurde erfolgreich geändert.', AlertTypes.info);
        this.passwordData = { oldPassword: '', newPassword: '' };
        this.passwordConfirmInput?.clear();
      });
    }
  }

  private setNewUserPassword() {
    if (this.userData) {
      this.editUserPassword = {
        title: this.userData.title,
        id: this.userData.id,
        email: this.userData.email,
        oldPassword: this.passwordData.oldPassword,
        newPassword: this.passwordData.newPassword,
        firstName: this.userData.firstName,
        lastName: this.userData.lastName,
        company: this.userData.company,
        isAuthorizedForDownloads: this.userData.isAuthorizedForDownloads,
        isAuthorizedForAlarms: this.userData.isAuthorizedForAlarms,
        isAlarmsActivated: this.userData.isAlarmsActivated,
        isAcousticAlarmsActivated: this.userData.isAcousticAlarmsActivated
      };
    }
  }

  public onTwoFactorSetupClicked(): void {
    this.isTwoFactorBusy = true;
    this.loginService.getAuthenticatorSetup().subscribe({
      next: async (setup) => {
        this.authenticatorSetup = setup;
        this.twoFactorSetupCode = '';

        try {
          this.authenticatorQrCodeDataUrl = await this.buildQrCodeDataUrl(setup.authenticatorUri);
          this.alertService.alert('Authenticator-Setup geladen. Scannen Sie den QR-Code und bestätigen Sie die Einrichtung mit Ihrem Code.', AlertTypes.info);
        } catch {
          this.authenticatorQrCodeDataUrl = null;
          this.alertService.alert('Authenticator-Setup geladen. Falls der QR-Code nicht angezeigt wird, verwenden Sie bitte den manuellen Secret Key.', AlertTypes.info);
        } finally {
          this.isTwoFactorBusy = false;
        }
      },
      error: () => {
        this.isTwoFactorBusy = false;
        this.authenticatorQrCodeDataUrl = null;
        this.alertService.alert('Authenticator-Setup konnte nicht geladen werden.', AlertTypes.error);
      }
    });
  }

  public onTwoFactorCodeChange(newValue: string): void {
    this.twoFactorSetupCode = newValue;
  }

  public onConfirmTwoFactorSetupClicked(): void {
    if (!this.authenticatorSetup) {
      this.alertService.alert('Bitte laden Sie zuerst das Authenticator-Setup.', AlertTypes.error);
      return;
    }

    if (!this.twoFactorSetupCode.trim()) {
      this.alertService.alert('Bitte geben Sie den Code aus Ihrer Authenticator-App ein.', AlertTypes.error);
      return;
    }

    if (!this.authenticatorSetup.twoFactorSetupToken) {
      this.alertService.alert('Die 2FA-Einrichtung ist abgelaufen. Bitte laden Sie das Setup erneut.', AlertTypes.error);
      return;
    }

    this.isTwoFactorBusy = true;
    this.loginService.enableAuthenticator({
      email: this.authenticatorSetup.email,
      twoFactorSetupToken: this.authenticatorSetup.twoFactorSetupToken,
      code: this.twoFactorSetupCode.trim()
    }).subscribe({
      next: () => {
        this.authenticatorSetup = undefined;
        this.authenticatorQrCodeDataUrl = null;
        this.twoFactorSetupCode = '';
        this.isTwoFactorEnabled = true;
        this.alertService.alert('2FA wurde erfolgreich aktiviert.', AlertTypes.success);
        this.refreshTwoFactorState(false);
      },
      error: (errorContents) => {
        this.isTwoFactorBusy = false;
        if (errorContents.status == 429) {
          this.alertService.alert('Zu viele ungültige Codes eingegeben. Bitte laden Sie das Setup neu.', AlertTypes.error);
          this.authenticatorSetup = undefined;
        }
        else {
          this.alertService.alert('Der Bestätigungscode ist ungültig oder der Setupprozess abgelaufen.', AlertTypes.error);
        }
        this.twoFactorSetupCode = '';
      }
    });
  }

  public onTwoFactorDisableClicked(): void {
    this.isTwoFactorBusy = true;
    this.loginService.disableAuthenticator().subscribe({
      next: () => {
        this.authenticatorSetup = undefined;
        this.authenticatorQrCodeDataUrl = null;
        this.twoFactorSetupCode = '';
        this.isTwoFactorEnabled = false;
        this.alertService.alert('2FA wurde deaktiviert.', AlertTypes.info);
        this.refreshTwoFactorState(false);
      },
      error: () => {
        this.isTwoFactorBusy = false;
        this.alertService.alert('2FA konnte nicht deaktiviert werden.', AlertTypes.error);
      }
    });
  }

  public onRevokeTermsClicked(): void {
    this.modalService.openDialog(
      'Zustimmung widerrufen? Sie werden abgemeldet.',
      'Nein', 'Ja'
    ).subscribe(confirmed => {
      if (!confirmed) {
        return;
      }
      this.userService.revokeTerms().subscribe({
        next: () => {
          this.alertService.alert('Ihre Datenschutz-Zustimmung wurde widerrufen.', AlertTypes.info);
          this.loginService.logout();
        },
        error: () => {
          this.alertService.alert('Der Widerruf konnte nicht gespeichert werden.', AlertTypes.error);
        }
      });
    });
  }

  public async onCopySecretKeyClicked(): Promise<void> {
    if (!this.authenticatorSetup?.secretKey) {
      return;
    }

    try {
      await navigator.clipboard.writeText(this.authenticatorSetup.secretKey);
      this.alertService.alert('Der Secret Key wurde in die Zwischenablage kopiert.', AlertTypes.success);
    } catch {
      this.alertService.alert('Der Secret Key konnte nicht kopiert werden.', AlertTypes.error);
    }
  }

  private refreshTwoFactorState(showError: boolean = false): void {
    this.loginService.getUserViaJwtToken().subscribe({
      next: (userData) => {
        this.applyUserData(userData);
        this.isTwoFactorBusy = false;
      },
      error: () => {
        this.isTwoFactorBusy = false;
        if (showError) {
          this.alertService.alert('Der aktuelle 2FA-Status konnte nicht geladen werden.', AlertTypes.error);
        }
      }
    });
  }

  private applyUserData(userData?: UserDataResponse): void {
    this.userData = userData;
    this.isTwoFactorEnabled = userData?.isTwoFactorEnabled ?? false;
  }

  private buildQrCodeDataUrl(authenticatorUri: string): Promise<string> {
    if (authenticatorUri.startsWith('data:image')) {
      return Promise.resolve(authenticatorUri);
    }

    return QRCode.toDataURL(authenticatorUri, {
      width: 240,
      margin: 1,
      errorCorrectionLevel: 'M'
    });
  }
}
